import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'

import { FEATURES, MODULES } from '../constants/template-info'
import QuinceApiError from '../domains/quince-api-error'
import wishListStateManager from '../redux/globalStateManager/WishlistStateManager'
import AuthUtil from '../utils/authUtil'
import { storefrontRuntimeHttpClient } from '../utils/HttpClient'

import { SharedWishlistRes, WishListItem } from './Wishlist.types'

const WISHLIST_LOCAL_STORAGE_KEY = 'wishlist'

class WishlistService {
  /**
   * A method that accepts a product and color and returns true if this product color combination is present in
   * local storage wishlist.
   */
  isItemInWishlist(shopifyProductId: number, color: string): boolean {
    const wishlistItemToBeSearched = new WishListItem({
      product_id: shopifyProductId,
      color: color,
    })
    return this.fetchUserWishListFromLocalStorage().some(wli => WishListItem.factEquals(wli, wishlistItemToBeSearched))
  }

  /**
   * On Login, combining wishlist from local storage and backend.
   */
  async updateWishListOnLoggedIn(): Promise<WishListItem[]> {
    const wishListItems = await this.fetchWishlistFromBackend(AuthUtil.getUser().email || '')
    const combinedWishListItems = getCombinedWishlist(
      this.getWishlistAfterRemovingStaleItems(wishListItems),
      this.fetchUserWishListFromLocalStorage(),
    )
    wishListStateManager.setWishlistTotalItemsCount(combinedWishListItems.length)
    // In case combined list is same as backend list, then skipping backend update.
    await this.updateWishListItems(combinedWishListItems, combinedWishListItems.length == wishListItems.length)
    return combinedWishListItems
  }

  /**
   * On Already logged in, return wishlist from backend.
   */
  async wishListAlreadyLoggedIn(email: string): Promise<WishListItem[]> {
    const wishListItems = await this.fetchWishlistFromBackend(email)
    const refinedWishList = this.getWishlistAfterRemovingStaleItems(wishListItems)
    await this.updateWishListItems(refinedWishList, refinedWishList.length == wishListItems.length)
    return refinedWishList
  }

  // TODO: Check types here
  getWishlistAfterRemovingStaleItems(wishListItems: WishListItem[]) {
    return wishListItems.filter((wishListItem: WishListItem) => {
      const colorName = wishListItem.color
      const colors = wishListItem.contentful_product?.colors
      if (colors) {
        const foundColor = colors.find(color => color.name === colorName)
        return foundColor !== undefined
      }
      return false
    })
  }

  getWishlistTotalItemsCount(): number {
    return this.fetchUserWishListFromLocalStorage().length
  }

  /**
   * This returns wishlist from Local Storage.
   */
  fetchUserWishListFromLocalStorage(): WishListItem[] {
    const wishListItemsString = window.localStorage.getItem(WISHLIST_LOCAL_STORAGE_KEY)
    let wishListItems = []
    try {
      if (wishListItemsString) {
        wishListItems = JSON.parse(wishListItemsString)
      }
      return wishListItems.map((wli: WishListItem) => {
        return new WishListItem({ product_id: wli.product_id, color: wli.color })
      })
    } catch (e) {
      // In case error in parsing of local storage key, then setting it empty.
      wishListItems = []
      this.updateWishListItems([], true).catch((e: unknown) => {
        console.error(e)
      })
    }
    return []
  }

  async addItemToWishlist(shopifyProductId: number, color: string) {
    const isPresentInWishList = this.isItemInWishlist(shopifyProductId, color)
    if (isPresentInWishList) {
      // If already present in wishlist, then ignoring
      return
    }
    const wishListItems = this.fetchUserWishListFromLocalStorage()
    wishListItems.push(
      new WishListItem({
        product_id: shopifyProductId,
        color: color,
      }),
    )
    await this.updateWishListItems(wishListItems)
  }

  async removeFromWishlist(shopifyProductId: number, color: string) {
    const isPresentInWishList = this.isItemInWishlist(shopifyProductId, color)
    if (!isPresentInWishList) {
      // If item is not present we dont need to remove it.
      return
    }
    const toRemoveWishListItem = new WishListItem({
      product_id: shopifyProductId,
      color: color,
    })
    await this.updateWishListItems(
      this.fetchUserWishListFromLocalStorage().filter(wli => !WishListItem.factEquals(wli, toRemoveWishListItem)),
    )
  }

  private async updateWishListItems(updatedWishListItems: WishListItem[], skipBackendUpdate = false) {
    const sanitizedWishList = updatedWishListItems.map(item => new WishListItem(item))
    updateWishListInLocalStorage(sanitizedWishList)

    const user = AuthUtil.getUser()
    if (AuthUtil.isLoggedIn() && user.email && !skipBackendUpdate) {
      await this.updateUserWishlistInBackend(sanitizedWishList, user.email)
    }
  }

  private updateUserWishlistInBackend = async (updatedWishListItems: WishListItem[], email: string) => {
    try {
      const userSession = await AuthUtil.getUserSession()
      await storefrontRuntimeHttpClient.get('/update-user-wishlist-v2', {
        params: {
          productIdColors: updatedWishListItems.map(wli => `${wli.product_id}_${wli.color}`).join(','),
        },
        headers: {
          Authorization: `${userSession?.idToken?.jwtToken}`,
        },
      })
    } catch (err) {
      const quinceApiError = new QuinceApiError({
        apiName: 'UPDATE_USER_WISHLIST',
        message: 'FAILED TO UPDATE WISHLIST',
        error: err as Error,
        data: {
          email,
        },
      })
      captureException(quinceApiError, {
        module: MODULES.WISHLIST,
        feature: FEATURES.UPDATE_WISHLIST,
      })
    }
  }

  /**
   * This method fetches wishlist item for a logged in user from redis cache.
   * @private
   */
  private fetchWishlistFromBackend = async (email: string): Promise<WishListItem[]> => {
    try {
      const userSession = await AuthUtil.getUserSession()
      const { data: wishlistItems } = await storefrontRuntimeHttpClient.get<WishListItem[]>('/get-user-wishlist-v2', {
        headers: {
          Authorization: `${userSession?.idToken?.jwtToken}`,
        },
      })
      return wishlistItems
    } catch (err) {
      const quinceApiError = new QuinceApiError({
        apiName: 'GET_USER_WISHLIST',
        message: 'FAILED TO GET USER WISHLIST',
        error: err as Error,
        data: {
          email,
        },
      })
      captureException(quinceApiError, {
        module: MODULES.WISHLIST,
        feature: FEATURES.FETCH_WISHLIST,
      })

      return []
    }
  }

  getWishlistToken = async () => {
    try {
      const userSession = await AuthUtil.getUserSession()
      const { data } = await storefrontRuntimeHttpClient.get('/get-wishlist-token', {
        headers: {
          Authorization: `${userSession?.idToken?.jwtToken}`,
        },
      })
      return data
    } catch (error) {
      console.error('Error While Getting Wishlist Token ', error)
      return {
        wishlist_token: '',
      }
    }
  }

  async getSharedWishlist(id: string): Promise<SharedWishlistRes> {
    try {
      const { data } = await storefrontRuntimeHttpClient.get('/get-shared-wishlist?wishlist_token=' + id)
      return data
    } catch (error) {
      console.error('error while getting the shared wishlist', error)
      return {}
    }
  }
}

const updateWishListInLocalStorage = (wishListItems: WishListItem[]) => {
  window.localStorage.setItem(WISHLIST_LOCAL_STORAGE_KEY, JSON.stringify(wishListItems))
}

/**
 * This method combines wishlist items from currentWishlistInBrowserStorage and wishlistFromApi and returns
 * combined wishlistItems.
 */
const getCombinedWishlist = (
  wishlistFromApi: WishListItem[],
  currentWishlistInBrowserStorage: WishListItem[],
): WishListItem[] => {
  const wishListIdSet = new Set<string>()
  const combinedList: WishListItem[] = []
  ;[...currentWishlistInBrowserStorage, ...wishlistFromApi].forEach(wli => {
    const id = WishListItem.getWishListId(wli)
    if (!wishListIdSet.has(id)) {
      wishListIdSet.add(id)
      combinedList.push(wli)
    }
  })
  return combinedList
}

const wishlistService = new WishlistService()

export default wishlistService
